import { useState, useMemo } from "react";
import { Loader2, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { PayloadToken } from "@/types/ai-prompt";
import { statusBadgeClass, statusLabel } from "./utils";

type PayloadLibraryViewProps = {
  tokens: PayloadToken[];
  isLoading: boolean;
};

type PayloadCategorySectionProps = {
  category: string;
  tokens: PayloadToken[];
};

function PayloadCategorySection({ category, tokens }: PayloadCategorySectionProps) {
  const [open, setOpen] = useState(true);

  const availableCount = tokens.filter((t) => t.status === "available").length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 w-full text-left py-1 group"
        >
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 text-muted-foreground transition-transform duration-150 shrink-0",
              !open && "-rotate-90"
            )}
          />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground group-hover:text-foreground transition-colors">
            {category}
          </span>
          <span className="text-xs text-muted-foreground font-normal">
            ({availableCount}/{tokens.length} available)
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <TooltipProvider delayDuration={300}>
          <div className="mt-1 rounded-lg border border-border overflow-hidden">
            {tokens.map((token, idx) => (
              <div
                key={token.key}
                className={cn(
                  "flex items-start gap-4 px-4 py-3",
                  idx !== tokens.length - 1 && "border-b border-border"
                )}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <code
                      className={cn(
                        "text-xs font-mono px-1.5 py-0.5 rounded shrink-0 mt-0.5 cursor-default border",
                        token.status === "available"
                          ? "text-green-700 dark:text-green-400 bg-green-500/10 border-green-500/20"
                          : token.status === "needs_work"
                          ? "text-yellow-700 dark:text-yellow-400 bg-yellow-500/10 border-yellow-500/20"
                          : "text-foreground bg-muted/50 border-transparent"
                      )}
                    >
                      {"{"}
                      {token.key}
                      {"}"}
                    </code>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p>
                      Copy <code className="font-mono text-[10px]">{`{${token.key}}`}</code> into any prompt to inject this data at runtime.
                    </p>
                  </TooltipContent>
                </Tooltip>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{token.description}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{token.data_source}</p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0 mt-0.5">
                  <span
                    className={cn(
                      "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
                      statusBadgeClass(token.status)
                    )}
                  >
                    {statusLabel(token.status)}
                  </span>
                  {token.resolver === 'runtime' && (
                    <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10 dark:bg-purple-400/10 dark:text-purple-400 dark:ring-purple-400/30">
                      Runtime
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TooltipProvider>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function PayloadLibraryView({ tokens, isLoading }: PayloadLibraryViewProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, PayloadToken[]>();
    for (const token of tokens) {
      const group = map.get(token.category) ?? [];
      group.push(token);
      map.set(token.category, group);
    }
    return map;
  }, [tokens]);

  const categories = useMemo(() => [...grouped.keys()], [grouped]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {categories.map((category) => {
        const categoryTokens = grouped.get(category) ?? [];
        return (
          <PayloadCategorySection key={category} category={category} tokens={categoryTokens} />
        );
      })}
    </div>
  );
}
